import React, { useEffect, useMemo, useState } from 'react';

import { SiteFooter } from '../../../components/SiteFooter/SiteFooter';
import { SiteNav } from '../../../components/SiteNav/SiteNav';
import type { Brand, Product, ShopCapsule, SocialLink, UserInfo } from '../../shop-builder/types';
import { AboutPanel } from './components/AboutPanel';
import { AthleteHero } from './components/AthleteHero';
import { FloatingFilters } from './components/FloatingFilters';
import { ProductCount } from './components/ProductCount';
import { ProductGrid } from './components/ProductGrid';
import { ShopAboutToggle, type ShopView } from './components/ShopAboutToggle';

const CAPSULE_QUERY_PARAM = 'capsule';

interface AthleteShopProps {
    csrfToken: string;
    athleteInfo: UserInfo;
    brands: (Brand | null)[];
    products: Product[];
    capsules: ShopCapsule[];
    socials: SocialLink[];
    splashRedesignEnabled: boolean;
}

export default function AthleteShop({
    csrfToken,
    athleteInfo,
    brands,
    products,
    capsules,
    socials,
    splashRedesignEnabled,
}: AthleteShopProps) {
    const [view, setView] = useState<ShopView>('shop');
    const [selectedCapsuleId, setSelectedCapsuleId] = useState<string | null>(() => {
        // Default landing view is All (null). Only narrow to a specific
        // capsule when ?capsule=<public_id> matches one in the list.
        if (typeof window === 'undefined' || capsules.length === 0) { return null; }
        const param = new URLSearchParams(window.location.search).get(CAPSULE_QUERY_PARAM)?.toLowerCase();
        if (!param || param === 'all') { return null; }
        const match = capsules.find((c) => c.public_id.toLowerCase() === param);
        return match ? match.public_id : null;
    });

    useEffect(() => {
        if (typeof window === 'undefined') { return; }
        const nextUrl = new URL(window.location.href);
        // Mirror the rest of the codebase: missing param == "all".
        // Strip the query when no capsule is active so /@slug stays clean;
        // only write the param when narrowing to a specific capsule.
        if (selectedCapsuleId) {
            nextUrl.searchParams.set(CAPSULE_QUERY_PARAM, selectedCapsuleId);
        } else {
            nextUrl.searchParams.delete(CAPSULE_QUERY_PARAM);
        }
        const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (nextPath !== currentPath) {
            window.history.replaceState(window.history.state, '', nextPath);
        }
    }, [selectedCapsuleId]);

    const cleanBrands = useMemo(
        () => brands.filter((b): b is Brand => b !== null),
        [brands],
    );

    const filteredProducts = useMemo(() => {
        if (!selectedCapsuleId) { return products; }
        const capsule = capsules.find((c) => c.public_id === selectedCapsuleId);
        if (!capsule) { return products; }
        return capsule.variant_ids.flatMap((variantId) => products.filter((p) => p.variant_id === variantId));
    }, [products, capsules, selectedCapsuleId]);

    return (
        <div className="ats-page">
            <div className="ats-nav">
                <SiteNav floating redesignEnabled={splashRedesignEnabled} />
            </div>
            <main className="ats-main">
                <div className="ats-shell">
                    <AthleteHero athleteInfo={athleteInfo} brands={cleanBrands} socials={socials} />
                    <div className="ats-toolbar">
                        <ShopAboutToggle
                            view={view}
                            onChange={setView}
                            ownerFirstName={athleteInfo.first_name}
                        />
                        {view === 'shop' && (
                            <ProductCount count={filteredProducts.length} />
                        )}
                    </div>
                    {view === 'shop' ? (
                        <ProductGrid
                            products={filteredProducts}
                            athleteSlug={athleteInfo.slug ?? null}
                        />
                    ) : (
                        <AboutPanel
                            bio={athleteInfo.bio ?? ''}
                            achievements={athleteInfo.achievements ?? []}
                        />
                    )}
                </div>
            </main>
            {view === 'shop' && (
                <FloatingFilters
                    mode="filter"
                    capsules={capsules}
                    selectedCapsuleId={selectedCapsuleId}
                    onSelect={setSelectedCapsuleId}
                />
            )}
            <SiteFooter csrfToken={csrfToken} redesignEnabled={splashRedesignEnabled} />
        </div>
    );
}
